import {
  addDays,
  compareAsc,
  format as dateformat,
  parseISO,
  differenceInDays,
  subDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Helpers } from '../Core';
import Constants from './constants';

/**
 *
 * @param year
 * @returns lunar dates by ywar
 * @see https://www.inf.ufrgs.br/~cabral/Pascoa.html
 */
const calculateLunarHoliday = (
  year: number,
): {
  carnival: Date;
  fridayGood: Date;
  easter: Date;
  corpusChristi: Date;
} => {
  let dayEaster = 0;
  let monthEaster = 0;

  // lista de feriados lunares
  const p = Constants.GAUSS_TABLE.find((y) => year >= y.yi && year <= y.yf)?.p;
  if (p) {
    const { x, y } = p;
    const a = year % 19;
    const b = year % 4;
    const c = year % 7;
    const d = (19 * a + x) % 30;
    const e = (2 * b + 4 * c + 6 * d + y) % 7;

    if (d + e > 9) {
      dayEaster = d + e - 9;
      monthEaster = 3; // ABRIL

      // Quando o domingo de Páscoa cair em Abril
      // e o dia for 26, corrige-se para uma semana antes, ou seja,
      // vai para dia 19;
      if (dayEaster === 26) {
        dayEaster = 19;
      }

      // Quando o domingo de Páscoa cair em Abril
      // e o dia for 25 e o termo "d" for igual a 28, simultaneamente com "a" maior que 10,
      // então o dia é corrigido para 18
      if (dayEaster === 25 && d === 28 && a > 10) {
        dayEaster = 18;
      }
    } else {
      dayEaster = d + e + 22;
      monthEaster = 2; // MARÇO
    }
  }
  const dateEaster = new Date(year, monthEaster, dayEaster);
  return {
    carnival: subDays(dateEaster, 47),
    fridayGood: subDays(dateEaster, 2),
    easter: dateEaster,
    corpusChristi: addDays(dateEaster, 60),
  };
};

export const getYearByDate = (d: string | Date): number => {
  let df = null;
  if (typeof d === 'string') {
    df = parseISO(d);
  } else {
    df = d;
  }
  return Number(dateformat(df, 'yyyy'));
};

type GetHolidaysType = {
  range: {
    /** Date native or DateTime ISO (yyyy-mm-ddThh:mi:ss) */
    start?: string | Date;
    /** Date native or DateTime ISO (yyyy-mm-ddThh:mi:ss) */
    end?: string | Date;
    /** Se verdadeiro, faz o range da data atual + 330 dias (desconsidera outros parametros) */
    searchFlight?: boolean;
  };
  /** Código internacional padrão ISO 2 (Exemplo BRASIL: BR) */
  countryCode?: 'BR' | 'US';
};

/**
 * @description Retorna os feriados por faixa de data (ano ou data padrão ISO)
 * @param GetHolidaysType
 * @returns holidays
 */
export const getHolidays = (params?: GetHolidaysType) => {
  const countryCode = params?.countryCode || 'BR';
  let holidays: {
    date: Date;
    name: string;
    type: string;
  }[] = [];

  let dateStart = new Date(); // inicia com data atual
  let yearStart = null;
  let dateEnd = new Date(dateStart.getFullYear(), 11, 31); // Data do fim do ano: 31/12/yyyy
  let yearEnd = null;

  if (params) {
    const { range } = params;

    if (range.searchFlight) {
      dateEnd = addDays(dateStart, 330); // regra Smiles. Ver onde vai ficar esse 330 dias
    } else {
      if (range?.start) {
        dateStart =
          typeof range.start === 'string' ? parseISO(range.start) : range.start;
      }
      if (range?.end) {
        dateEnd =
          typeof range.end === 'string' ? parseISO(range.end) : range.end;
      }
    }
  }

  yearStart = getYearByDate(dateStart);
  yearEnd = getYearByDate(dateEnd);

  if (dateStart.getTime() > dateEnd.getTime()) {
    throw new Error('Data inicial maior que data final');
  }

  const rangeDate = (dateHoliday: Date) =>
    dateHoliday.getTime() >= dateStart.getTime() &&
    dateHoliday.getTime() <= dateEnd.getTime();

  for (let year = yearStart; year <= yearEnd; year++) {
    // lista de feriados fixos
    const holidaysByYear = [];
    Constants.HOLIDAY_FIXED.forEach((ff) => {
      const dateHolidayFixed = new Date(year, ff.MONTH, ff.DAY);
      if (rangeDate(dateHolidayFixed)) {
        holidaysByYear.push({
          date: new Date(year, ff.MONTH, ff.DAY),
          name: ff.NAME[countryCode],
          type: 'fixed',
        });
      }
    });

    const { carnival, fridayGood, easter, corpusChristi } =
      calculateLunarHoliday(year);

    if (rangeDate(carnival)) {
      holidaysByYear.push({
        date: carnival,
        name: Constants.HOLIDAY_LUNAR.CA[countryCode],
        type: 'lunar',
      });
    }

    if (rangeDate(fridayGood)) {
      holidaysByYear.push({
        date: fridayGood,
        name: Constants.HOLIDAY_LUNAR.GF[countryCode],
        type: 'lunar',
      });
    }

    if (rangeDate(easter)) {
      holidaysByYear.push({
        date: easter,
        name: Constants.HOLIDAY_LUNAR.ES[countryCode],
        type: 'lunar',
      });
    }

    if (rangeDate(corpusChristi)) {
      holidaysByYear.push({
        date: corpusChristi,
        name: Constants.HOLIDAY_LUNAR.CC[countryCode],
        type: 'lunar',
      });
    }

    holidays = holidays.concat(holidaysByYear);
  }

  return holidays.sort((a, b) => a.date.getTime() - b.date.getTime());
};

type GroupByFormattedDateOptions = {
  locale: 'pt-BR';
};

/**
 *
 * @param data
 * @param attr
 * @param format by date-fns format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const groupByFormattedDate = <T = any, K extends keyof T = any>(
  data: T[],
  attr: K,
  format: string,
  { locale }: GroupByFormattedDateOptions = { locale: 'pt-BR' },
): { [key: string]: T[] } => {
  // this gives an object with dates as keys
  const dataGroups: Map<string, T[]> = new Map<string, T[]>();
  const localeJSON = locale === 'pt-BR' ? ptBR : undefined;

  data.forEach((item: T) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = item[attr] as any;
    const groupDate = dateformat(value, format, { locale: localeJSON });
    let actualEntries = dataGroups.get(groupDate);
    if (!actualEntries) {
      actualEntries = [];
    }
    actualEntries.push(item);
    dataGroups.set(groupDate, actualEntries);
  });

  return Helpers.mapToObject(dataGroups);
};

/**
 * If `date` param is within the last week, the return is that day in string format
 * Else, it is the date itself, in format dd/MM/yyyy HH:mm
 * @param {Date} date - date can't be after today
 */
export const getLastWeekAsDayString = (date: Date | number) => {
  const today = new Date().setHours(23, 59, 59);

  const isAfterToday = compareAsc(date, today) === 1;
  if (isAfterToday) {
    throw new Error("date param is after today's date");
  }

  const diffInDays = differenceInDays(today, date);
  let result;
  const dayStringFormat = 'EEEE'; // see https://www.unicode.org/reports/tr35/tr35-dates.html#dfst-weekday
  switch (true) {
    case diffInDays <= 7 && diffInDays > 1:
      result = dateformat(date, `${dayStringFormat}, HH:mm`, {
        locale: ptBR,
      }).replace(
        new RegExp('(^\\w)|-(\\w)', 'g'), // @example quinta-feira -> Quinta-Feira
        (matched) => matched.toUpperCase(),
      );
      break;
    case diffInDays === 1:
      result = `Ontem, ${dateformat(date, 'HH:mm')}`;
      break;
    case diffInDays === 0:
      result = `Hoje, ${dateformat(date, 'HH:mm')}`;
      break;
    default:
      result = dateformat(date, 'dd/MM/yyyy, HH:mm');
      break;
  }

  return result.replace(/:/, 'h');
};

export default {
  groupByFormattedDate,
  getHolidays,
  getYearByDate,
  Constants,
  getLastWeekAsDayString,
};
